import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { conversationsKeys } from '@/entities/conversations';
import { API_TAGS, queryClient } from '@/shared/api';
import {
  createAttribute,
  deleteAttribute,
  fetchAllAttributes,
  fetchAttributeByCode,
  fetchAttributeNames,
  fetchAttributeStructs,
  fetchAttributes,
  restoreAttribute,
  searchAttributeReferences,
  updateAttribute,
} from './api';
import type {
  AttributeCreate,
  AttributeDeleteParams,
  AttributeFetchOptions,
  AttributeGetParams,
  AttributeListParams,
  AttributeNameListParams,
  AttributeRestoreParams,
  AttributeStructListParams,
  AttributeUpdate,
  AttributeUpdateParams,
} from './types';

/**
 * Query keys for attributes
 */
export const attributeKeys = {
  all: [API_TAGS.ATTRIBUTES] as const,
  lists: () => [...attributeKeys.all, 'list'] as const,
  list: (params?: AttributeListParams) => [...attributeKeys.lists(), { ...params }] as const,
  fullList: (params?: AttributeListParams) => [...attributeKeys.lists(), 'full', { ...params }] as const,
  names: () => [...attributeKeys.all, 'names'] as const,
  namesList: (params?: AttributeNameListParams) => [...attributeKeys.names(), { ...params }] as const,
  details: () => [...attributeKeys.all, 'detail'] as const,
  detail: (code: string, params?: AttributeGetParams) => [...attributeKeys.details(), code, params] as const,
  structs: () => [...attributeKeys.all, 'structs'] as const,
  structsList: (params?: AttributeStructListParams) => [...attributeKeys.structs(), { ...params }] as const,
  references: () => [...attributeKeys.all, 'references'] as const,
  referenceSearch: (query: string) => [...attributeKeys.references(), query] as const,
};

/**
 * Hook to fetch attributes with values
 */
export const useAttributes = (params?: AttributeListParams, enabled = true) => {
  return useQuery({
    queryKey: attributeKeys.list(params),
    queryFn: () => fetchAttributes(params),
    enabled,
  });
};

/**
 * Хук всего набора атрибутов под отбором: набор доходит до последнего атрибута, сколько бы окон
 * выдачи он ни занимал. Нужен выбору из справочника, которому первое окно за весь набор выдавать нельзя.
 */
export const useAllAttributes = (params: Omit<AttributeListParams, 'limit' | 'offset'> = {}, enabled = true) => {
  return useQuery({
    queryKey: attributeKeys.fullList(params),
    queryFn: () => fetchAllAttributes(params),
    enabled,
  });
};

/**
 * Hook to fetch attributes with infinite scroll
 */
export const useInfiniteAttributes = (params?: AttributeListParams & { limit?: number; offset?: number }) => {
  const limit = params?.limit || 50;
  const initialPageParam = Math.floor((params?.offset || 0) / limit);

  const query = useInfiniteQuery({
    queryKey: attributeKeys.list(params),
    queryFn: ({ pageParam }) => fetchAttributes({ ...params, limit, offset: pageParam * limit }),
    initialPageParam,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      return lastPage.total <= limit * (lastPageParam + 1) ? undefined : lastPageParam + 1;
    },
  });

  const data = useMemo(
    () => ({ total: query.data?.pages[0]?.total, attributes: query.data?.pages.flatMap((p) => p.attributes) }),
    [query.data],
  );

  return {
    ...query,
    data,
  };
};

/**
 * Hook to fetch attribute names (without values)
 */
export const useAttributeNames = (params?: AttributeNameListParams) => {
  return useQuery({
    queryKey: attributeKeys.namesList(params),
    queryFn: () => fetchAttributeNames(params),
  });
};

/**
 * Hook to fetch a single attribute by code
 * @param code - Attribute code
 * @param params - Optional query parameters (parentStructCode)
 * @param options - Optional transport options (silentError)
 */
export const useAttribute = (code?: string, params?: AttributeGetParams, options?: AttributeFetchOptions) => {
  return useQuery({
    queryKey: attributeKeys.detail(code!, params),
    queryFn: () => fetchAttributeByCode(code!, params, options),
    enabled: typeof code === 'string' && code.length > 0,
  });
};

/**
 * Сбрасывает данные карточек диалогов: какие атрибуты карточка показывает без значения,
 * зависит от настройки атрибута и от того, действует ли он. Вместе с карточками сбрасывается
 * перечень атрибутов диалогов — по нему строки карточки раскладываются по группам, и без сброса
 * строка нового или восстановленного атрибута попадала бы в «Без группы».
 */
const invalidateConversationCards = () => {
  queryClient.invalidateQueries({ queryKey: conversationsKeys.details() });
  queryClient.invalidateQueries({ queryKey: conversationsKeys.attributes() });
};

/**
 * Hook to create an attribute
 */
export const useCreateAttribute = () => {
  return useMutation({
    mutationFn: (attribute: AttributeCreate) => createAttribute(attribute),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attributeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: attributeKeys.names() });
      invalidateConversationCards();
    },
  });
};

/**
 * Hook to update an attribute
 */
export const useUpdateAttribute = () => {
  return useMutation({
    mutationFn: ({
      code,
      attribute,
      params,
    }: {
      code: string;
      attribute: AttributeUpdate;
      params?: AttributeUpdateParams;
    }) => updateAttribute({ code, attribute, params }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: attributeKeys.detail(variables.code, variables.params) });
      queryClient.invalidateQueries({ queryKey: attributeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: attributeKeys.names() });
      invalidateConversationCards();
    },
  });
};

/**
 * Hook to delete an attribute
 */
export const useDeleteAttribute = () => {
  return useMutation({
    mutationFn: ({ code, params }: { code: string; params?: AttributeDeleteParams }) => deleteAttribute(code, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attributeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: attributeKeys.names() });
      invalidateConversationCards();
    },
  });
};

/**
 * Hook to restore a soft-deleted attribute
 */
export const useRestoreAttribute = () => {
  return useMutation({
    mutationFn: ({ code, params }: { code: string; params?: AttributeRestoreParams }) => restoreAttribute(code, params),
    onSuccess: () => {
      // Инвалидируем и деталь (карточка выходит из readonly), и списки/имена (атрибут снова действует)
      queryClient.invalidateQueries({ queryKey: attributeKeys.details() });
      queryClient.invalidateQueries({ queryKey: attributeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: attributeKeys.names() });
      invalidateConversationCards();
    },
  });
};

/**
 * Hook to fetch attribute structs
 * @param params - Optional query parameters (limit, offset)
 */
export const useAttributeStructs = (params?: AttributeStructListParams) => {
  return useQuery({
    queryKey: attributeKeys.structsList(params),
    queryFn: () => fetchAttributeStructs(params),
  });
};

/**
 * Hook to search attribute references for rule field
 * @param query - Search query string
 * @param enabled - Whether the query should be enabled
 */
export const useAttributeReferenceSearch = (query: string, enabled = true) => {
  return useQuery({
    queryKey: attributeKeys.referenceSearch(query),
    queryFn: () => searchAttributeReferences(query),
    enabled: enabled && query.length > 0,
  });
};
